import React, { useEffect, useMemo, useState } from "react";
import { Loader2, ChevronLeft, ChevronRight, Stethoscope, FileSignature, ClipboardList } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { getCalendar } from "@/lib/api";

const TODAY = new Date();

const monthLabel = (y, m) =>
  new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });

const daysInMonth = (y, m) => new Date(y, m, 0).getDate();
const firstWeekday = (y, m) => new Date(y, m - 1, 1).getDay(); // 0=Sun
const pad = (n) => String(n).padStart(2, "0");

const KIND_META = {
  service_request: { icon: Stethoscope, color: "#0F6D5D", bg: "rgba(15,109,93,0.10)", label: "Service" },
  contract_end: { icon: FileSignature, color: "#9B1C1C", bg: "rgba(155,28,28,0.08)", label: "Contract end" },
  task: { icon: ClipboardList, color: "#7A5E12", bg: "rgba(212,175,55,0.18)", label: "Task" },
};

export default function CalendarPanel({ onSessionExpired }) {
  const [year, setYear] = useState(TODAY.getFullYear());
  const [month, setMonth] = useState(TODAY.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [byDay, setByDay] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getCalendar(`${year}-${pad(month)}`);
        if (!cancelled) setByDay(data.by_day || {});
      } catch (err) {
        if (err?.response?.status === 401) { onSessionExpired?.(); return; }
        toast.error("Failed to load calendar.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [year, month, onSessionExpired]);

  const grid = useMemo(() => {
    const n = daysInMonth(year, month);
    const first = firstWeekday(year, month);
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= n; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const prev = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1);
  };
  const today = () => { setYear(TODAY.getFullYear()); setMonth(TODAY.getMonth() + 1); };

  const totalEvents = Object.values(byDay).reduce((a, b) => a + b.length, 0);

  return (
    <div className="space-y-6" data-testid="calendar-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>Calendar</h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>
            Service requests, contract renewals, and task due-dates in one view.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prev} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-display text-base font-semibold min-w-[160px] text-center" data-testid="cal-month-label" style={{ color: "#0B2545" }}>
            {monthLabel(year, month)}
          </div>
          <Button variant="outline" size="sm" onClick={next} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={today} data-testid="cal-today">Today</Button>
        </div>
      </div>

      <div className="flex gap-3 text-xs flex-wrap">
        {Object.entries(KIND_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full"
                style={{ background: m.bg, color: m.color }}>
            <m.icon className="h-3 w-3" /> {m.label}
          </span>
        ))}
        <span className="ml-auto" style={{ color: "#64748B" }}>{totalEvents} event{totalEvents === 1 ? "" : "s"} this month</span>
      </div>
      <p className="text-xs -mt-2" style={{ color: "#94A3B8" }} data-testid="calendar-hint">
        Tip: set a lead&apos;s status to <span className="font-medium" style={{ color: "#0F6D5D" }}>Scheduled</span> in
        the Leads panel to place it on the calendar on the date the customer requested.
      </p>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>
      ) : (
        <div className="rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
          <div className="grid grid-cols-7 border-b text-[11px] uppercase tracking-[0.16em] font-medium"
               style={{ borderColor: "#E2E8F0", background: "#F8FAFC", color: "#64748B" }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
              <div key={d} className="px-3 py-2 text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {grid.map((d, i) => {
              if (d === null) return <div key={i} className="border-t border-r p-2 min-h-[100px]" style={{ borderColor: "#F1F5F9", background: "#FBFCFE" }} />;
              const dateStr = `${year}-${pad(month)}-${pad(d)}`;
              const events = byDay[dateStr] || [];
              const isToday = dateStr === `${TODAY.getFullYear()}-${pad(TODAY.getMonth() + 1)}-${pad(TODAY.getDate())}`;
              return (
                <div key={i} className="border-t border-r p-2 min-h-[100px]"
                     style={{ borderColor: "#F1F5F9", background: isToday ? "rgba(212,175,55,0.06)" : "#FFFFFF" }}
                     data-testid={`day-${dateStr}`}>
                  <div className="text-[11px] font-semibold mb-1"
                       style={{ color: isToday ? "#7A5E12" : "#0B2545" }}>{d}</div>
                  <div className="space-y-1">
                    {events.slice(0, 3).map((e, idx) => {
                      const meta = KIND_META[e.kind] || KIND_META.task;
                      return (
                        <div key={idx} className="text-[10px] rounded px-1.5 py-0.5 truncate"
                             style={{ background: meta.bg, color: meta.color }}
                             title={e.title}>
                          {e.title}
                        </div>
                      );
                    })}
                    {events.length > 3 && (
                      <div className="text-[10px]" style={{ color: "#64748B" }}>+{events.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
